import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Space, Dropdown, Select, Tag, Avatar, Tooltip, Typography } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { EditOutlined, LayoutOutlined, FolderOpenOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import FilterBar, { FilterField } from 'components/Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';
const { Option } = Select;

const SEARCH_DEBOUNCE_MS = 300;

const URL_FILTERS = {
  search: { type: 'string' },
  status: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 20 },
};

const Projects = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'card'
  // Search, status and page live in the URL; filter changes go back to page 1.
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const searchTerm = filters.search || '';
  const setSearchTerm = (value) => {
    if (value !== searchTerm) setFilters({ search: value, page: 1 });
  };
  const [searchInput, setSearchInput] = useState(searchTerm);
  useEffect(() => setSearchInput(searchTerm), [searchTerm]);
  const statusFilter = filters.status || '';
  const setStatusFilter = (value) => setFilters({ status: value, page: 1 });
  const [total, setTotal] = useState(0);
  const pagination = { current: filters.page, pageSize: Math.min(filters.pageSize, MAX_PAGE_SIZE), total };
  const debounceRef = useRef(null);

  // Debounce keystrokes into searchTerm; AntD Search's onChange fires per character.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    fetchProjects(pagination.current, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, pagination.current, pagination.pageSize]);

  const fetchProjects = async (page, pageSize) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', pageSize);
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`/project_board/projects/?${params.toString()}`);
      const data = response.data;
      if (Array.isArray(data)) {
        setProjects(data);
        setTotal(data.length);
      } else {
        setProjects(data.results || []);
        setTotal(data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Code',
      dataIndex: 'project_code',
      key: 'project_code',
      width: 100,
      render: (text) => <Tag color="geekblue" style={{ fontSize: 11, margin: 0 }}>{text}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
      render: (text, record) => (
        <a onClick={() => navigate(`/tasks?project_id=${record.id}`)} style={{ fontSize: 13 }}>{text}</a>
      ),
    },
    {
      title: 'Owner',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 120,
      ellipsis: true,
      render: (text) => <span style={{ fontSize: 12 }}>{text || '-'}</span>,
    },
    {
      title: 'Team',
      dataIndex: 'members',
      key: 'members',
      width: 120,
      render: (members) => {
        if (!members || members.length === 0) {
          return <Typography.Text type="secondary" style={{ fontSize: 12 }}>-</Typography.Text>;
        }
        return (
          <Tooltip
            title={
              <div>
                {members.map((m, idx) => (
                  <div key={m.id || idx} style={{ whiteSpace: 'nowrap' }}>
                    {m.user_name} <span style={{ opacity: 0.7 }}>({m.role_display})</span>
                  </div>
                ))}
              </div>
            }
            placement="top"
          >
            <span style={{ cursor: 'pointer', display: 'inline-block' }}>
              <Avatar.Group maxCount={3} size="small">
                {members.map((member) => (
                  <Avatar
                    key={member.id}
                    style={{
                      backgroundColor: ['#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#fa8c16'][
                        member.user_name?.charCodeAt(0) % 5
                      ],
                      fontSize: 10
                    }}
                    size="small"
                  >
                    {member.user_name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                  </Avatar>
                ))}
              </Avatar.Group>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status_display',
      key: 'status',
      width: 100,
      render: (text) => <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>{text}</Tag>,
    },
    {
      title: 'Tasks',
      key: 'tasks',
      width: 80,
      render: (_, record) => (
        <span style={{ fontSize: 12 }}>{record.completed_tasks}/{record.total_tasks}</span>
      ),
    },
    {
      title: 'Due',
      dataIndex: 'due_date',
      key: 'due_date',
      width: 90,
      render: (date, record) => {
        if (!date) return <span style={{ fontSize: 11, color: '#999' }}>-</span>;
        const isOverdue = record.is_overdue;
        return (
          <span style={{ fontSize: 11, color: isOverdue ? '#ff4d4f' : 'inherit' }}>
            {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size={2}>
          <Button
            type="text"
            icon={<FolderOpenOutlined style={{ fontSize: 12 }} />}
            size="small"
            onClick={() => navigate(`/projects/view/${record.id}`)}
            title="View Project"
          />
          <Button
            type="text"
            icon={<UnorderedListOutlined style={{ fontSize: 12 }} />}
            size="small"
            onClick={() => navigate(`/tasks?project_id=${record.id}`)}
            title="View Tasks"
          />
          {record.is_owner && (
            <Button
              type="text"
              icon={<EditOutlined style={{ fontSize: 12 }} />}
              size="small"
              onClick={() => navigate(`/projects/edit/${record.id}`)}
              title="Edit Project"
            />
          )}
        </Space>
      ),
    },
  ], [navigate]);

  const viewMenuItems = [
    { key: 'list', label: 'List View', onClick: () => setCurrentView('list') },
    { key: 'card', label: 'Card View', onClick: () => setCurrentView('card') },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <FilterBar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          onSearch={setSearchTerm}
          searchPlaceholder="Search projects"
          activeCount={statusFilter ? 1 : 0}
          onClear={() => setStatusFilter('')}
          title="Filter Projects"
          addLabel="New Project"
          onAdd={() => navigate('/projects/new')}
        >
          <FilterField label="Status">
            <Select
              placeholder="Filter by status"
              allowClear
              value={statusFilter || undefined}
              onChange={(v) => setStatusFilter(v || '')}
            >
              <Option value="PLANNING">Planning</Option>
              <Option value="IN_PROGRESS">In Progress</Option>
              <Option value="ON_HOLD">On Hold</Option>
              <Option value="COMPLETED">Completed</Option>
              <Option value="CANCELLED">Cancelled</Option>
            </Select>
          </FilterField>
        </FilterBar>
        <Space>
          <Dropdown menu={{ items: viewMenuItems }}>
            <Button icon={<LayoutOutlined />}>
              {currentView === 'list' ? 'List View' : 'Card View'}
            </Button>
          </Dropdown>
        </Space>
      </div>

      {currentView === 'list' ? (
        <Table
          columns={columns}
          dataSource={projects}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 800 }}
          pagination={{
            size: 'small',
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: ['10', '20', '50', '100'],
            // A new page size starts again from page 1
            onChange: (page, pageSize) =>
              setFilters({ page: pageSize !== pagination.pageSize ? 1 : page, pageSize }),
          }}
        />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {projects.map(project => (
            <Card
              key={project.id}
              title={project.name}
              hoverable
              onClick={() => navigate(`/tasks?project_id=${project.id}`)}
              extra={project.is_owner ? <a onClick={(e) => { e.stopPropagation(); navigate(`/projects/edit/${project.id}`); }}>Edit</a> : null}
              cover={
                project.cover_image_url && (
                  <img alt={project.name} src={project.cover_image_url} style={{ height: '150px', objectFit: 'cover' }} />
                )
              }
            >
              <p><strong>Owner:</strong> {project.owner_name}</p>
              <p><strong>Status:</strong> <Tag color="blue">{project.status_display}</Tag></p>
              <p><strong>Progress:</strong> {project.progress_percentage}%</p>
              <p><strong>Tasks:</strong> {project.completed_tasks}/{project.total_tasks}</p>
              {project.members && project.members.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <strong>Team: </strong>
                  <Tooltip
                    title={
                      <div>
                        {project.members.map((m, idx) => (
                          <div key={m.id || idx} style={{ whiteSpace: 'nowrap' }}>
                            {m.user_name} <span style={{ opacity: 0.7 }}>({m.role_display})</span>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                      <Avatar.Group maxCount={4} size="small">
                        {project.members.map((member) => (
                          <Avatar
                            key={member.id}
                            style={{
                              backgroundColor: ['#1890ff', '#52c41a', '#722ed1', '#eb2f96', '#fa8c16'][
                                member.user_name?.charCodeAt(0) % 5
                              ],
                              fontSize: 10
                            }}
                            size="small"
                          >
                            {member.user_name?.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)}
                          </Avatar>
                        ))}
                      </Avatar.Group>
                    </span>
                  </Tooltip>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Projects;
